<template>
    <DropZone v-if="show_drop_zone" class="drop-area" :mimetypes="types" @files-dropped="addFiles"
        #default="{ dropZoneActive, dropZoneVariant }" @click="SelectFile()">
        <div v-if="dropZoneActive" class="my-1">
            <div v-if="dropZoneVariant == 'success'" v-html="drop_icon_success" style="width:6em;height: 6em;"></div>
            <div v-else-if="dropZoneVariant == 'warning'" v-html="drop_icon_warning" style="width:6em;height: 6em;"></div>
            <div v-else-if="dropZoneVariant == 'danger'" v-html="drop_icon_danger" style="width:6em;height: 6em;"></div>
        </div>
        <div class="my-1" v-else>
            <div v-html="upload_icon" style="width:6em;height: 6em;"></div>
        </div>
        <div v-if="dropZoneActive">
            <d-text v-if="dropZoneVariant == 'success'">Drop_Files_Here</d-text>
            <d-text v-else-if="dropZoneVariant == 'warning'">Drop_Files_Some_Invalid</d-text>
            <d-text v-else-if="dropZoneVariant == 'danger'">Drop_Files_All_Invalid</d-text>
        </div>
        <div v-else><b><d-text>Button_Browse</d-text></b> <d-text>Or</d-text> <d-text>Drag_And_Drop_Files_Here</d-text>
        </div>
        <div class="upload-input">
            <input v-if="capture" type="file" :multiple="multiple" :capture="capture" @change="onInputChange"
                :id="id" />
            <input v-else type="file" :multiple="multiple" @change="onInputChange" :id="id" />
        </div>
    </DropZone>
    <div class="file-container container my-4" v-if="showfiles">
        <div class="current-files my-2">
            <div class="text-end" v-if="files.length > 0">
                <d-button variant="link" class="text-info"
                    @click="ClearAllFiles"><d-text>Button_Clear_All</d-text></d-button>
            </div>
            <d-alert variant="info" v-for="file of files" :key="file.id" dismissible @dissmissed="removeFile(file)">
                <d-row class="align-items-center">
                    <d-col v-if="preview && file.type.includes('image')" cols="auto">
                        <img class="preview-img" :src="file.url" :alt="file.name" :title="file.name" />
                    </d-col>
                    <d-col cols="auto">
                        <LoaderLottie v-if="file.status == 'loading'" :size="40"></LoaderLottie>
                        <div class="waiting" v-else-if="file.status == undefined" title="Väntar på uppladdning"
                            v-html="wait_icon" style="width:1em;height: 1.5em;"></div>
                    </d-col>
                    <d-col cols="auto">
                        {{ file.name }} ({{ file.sizeformat }})
                    </d-col>
                </d-row>
            </d-alert>
            <div v-if="!autoupload && !hideuploadbtn" v-show="files.length" class="text-center">
                <d-button variant="primary" @click="doUpload"><d-text>Button_Upload</d-text></d-button>
            </div>
        </div>
        <div class="completed-files my-2" v-if="completedfiles.length">
            <hr />
            <d-alert variant="success" :autodissmiss="dissmisscomplete" v-for="completedFile of completedfiles"
                :key="completedFile.id">{{ completedFile.name }} {{ completedFile.sizeformat }}</d-alert>
        </div>
        <div class="failed-files my-2" v-if="errorfiles.length">
            <hr />
            <div class="text-end">
                <d-button variant="link" class="text-danger"
                    @click="ClearErrors"><d-text>Button_Clear_All</d-text></d-button>
            </div>
            <d-alert variant="danger" dismissible @dissmissed="removeErrorFile(errorFile)" v-for="errorFile of errorfiles" :key="errorFile.id">{{ errorFile.name }}: {{ errorFile.error }}</d-alert>
        </div>
    </div>
</template>

<script setup>

import { ref, computed, defineProps, defineEmits, defineExpose, getCurrentInstance } from 'vue'
import DropZone from './Upload/DropZone.vue'
import LoaderLottie from "@/dod/components/Lottie/DLottieLoader.vue"
import dicons from './../enums/dicons';

const emit = defineEmits(['file-complete', 'files-complete', 'file-failed', 'files-failed', 'uploadStarted'])

const upload_icon = dicons.upload_icons.upload;
const drop_icon_success = dicons.upload_icons.drop_success;
const drop_icon_warning = dicons.upload_icons.drop_warning;
const drop_icon_danger = dicons.upload_icons.drop_danger;
const wait_icon = dicons.upload_icons.wait;

const props = defineProps({
    preview: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    hideonstart: { type: Boolean, default: false },
    autoupload: { type: Boolean, default: false },
    hideuploadbtn: { type: Boolean, default: false },
    uploadUrl: { type: String, required: true },
    dissmisscomplete: { type: Number, default: -1 },
    maxbytes: { type: Number, required: false, default: -1 },
    types: { type: Array, default: null },
    capture: { type: String, default: null },
    showfiles: { type: Boolean, default: true },
    headers: { type: Object }
})

const id = ref(`dod-file-upload-${Math.random() * 3000000}`)
const { appContext } = getCurrentInstance()
const dfilehelper = appContext.config.globalProperties.$dfilehelper;


const completedfiles = ref([])
const files = ref([])
const errorfiles = ref([])
const started = ref(false)

const show_drop_zone = computed(() => {
    var show = true;
    if(started.value && props.hideonstart)
        show = false;
    if(!props.autoupload && files.value.length > 0)
        show = false;
    return show;
})

const nrFiles = computed(() => files.value.length)

async function addFiles(newFiles) {

    for (var i = 0; i < newFiles.length; i++) {
        var file = new UploadableFile(newFiles[i]);
        var invalid = ValidateFile(file);
        file.error = invalid;
        invalid ? errorfiles.value.push(file) : files.value.push(file);
    }
    if (props.autoupload)
        doUpload();
}

// Expose the method to allow the parent to access it
defineExpose({
    doUpload,  
    ClearAllFiles,
    ClearCompleted,
    ClearErrors,
    nrFiles
});

async function doUpload() {
    if(files.value.length <= 0)
        return;
    emit("uploadStarted", files.value);
    started.value = true;
    await uploadFiles(files.value);        
    if(completedfiles.value.length > 0)
        emit("files-complete", completedfiles.value);
    if(errorfiles.value.length > 0)
        emit("files-failed", errorfiles.value);
    started.value = false;
    
}

function ValidateFile(file) {

    if (fileExists(file.id))
        return "File_Validation_Exists";
    if (!CheckFileType(file.type))
        return `Ogiltig filtyp: ${file.type} `;
    // filesize
    if (!CheckFileSize(file.size))
        return `Filen är för stor. Filens storlek: ${file.sizeformat}. Max tillåtna storlek: ${dfilehelper.formatedFilesize(props.maxbytes)}`;
    return null;
}

function fileExists(otherId) {
    return files.value.some(({ id }) => id === otherId)
}

function CheckFileSize(size) {
    if (props.maxbytes < 0)
        return true;
    return size < props.maxbytes;
}

function CheckFileType(type) {
    var types = props.types;
    if (types == null || types.length <= 0)
        return true;

    for (var i = 0; i < types.length; i++) {
        if (type.includes(types[i]))
            return true;
    }
    return false;
}

function removeFile(file) {
    const index = files.value.indexOf(file)
    if (index > -1) files.value.splice(index, 1)
}

function ClearAllFiles() {
    files.value = [];
}

function removeErrorFile(file) {
    const index = errorfiles.value.indexOf(file)
    if (index > -1) errorfiles.value.splice(index, 1)
}

function ClearErrors() {
    errorfiles.value = [];
}

function ClearCompleted() {
    completedfiles.value = [];
}

function onInputChange(e) {

    addFiles(e.target.files)
    e.target.value = null // reset so that selecting the same file again will still cause it to fire this change
}

function SelectFile() {
    document.getElementById(id.value).click();
}

async function getUrls(files) {
    // set up the request data
    let file_names = [];
    // track status and upload file
    files.map((file) => { file_names.push(file.name); file.status = 'loading' });
    var post_data = {
        FilesToUpload: file_names
    }
    let response = await fetch(props.uploadUrl, { method: 'POST', body: JSON.stringify(post_data), headers: props.headers });
    var json = await response.json();
    return json?.Data?.Files ?? {};
}

async function uploadFile(file, presigned_url) {
    // set up the request data
    var encodedFilename = encodeURI(file.name);
    let file_headers = {
        "Content-Type": file.type,
        "Content-Disposition": "inline; filename=" + encodedFilename + "; filename*=utf-8''" + encodedFilename
    }
    //let allheaders = { ...file_headers, ...headers };
    let response = await fetch(presigned_url, { method: 'PUT', body: file.file, mode: "cors", headers: file_headers })
    // change status to indicate the success of the upload request
    file.status = response.ok
    file.url = presigned_url;

    if (file.status == true) {
        completedfiles.value.push(file);
        emit("file-complete", file);
    }
    else {
        errorfiles.value.push(file);
        emit("file-failed", file);
    }

    removeFile(file);

    return response
}


async function uploadFiles(files) {
    // get presigned_urls
    let urls = await getUrls(files);
    return Promise.all(files.map((file) => uploadFile(file, urls[file.name])))
}

class UploadableFile {
    constructor(file) {        
        this.file = file;        
        this.name = dfilehelper.getFilename(file);
        this.type = dfilehelper.getContentType(file);
        this.size = dfilehelper.getFilesize(file);
        this.sizeformat = dfilehelper.formatedFilesize(this.size);
        this.error = null;
        this.url = URL.createObjectURL(file)
        this.status = null
        this.id = `${this.name}-${this.size}-${file.lastModified}-${this.type}`
    }
}

</script>


<style lang="scss" >
.image-list {
    display: flex;
    list-style: none;
    flex-wrap: wrap;
    padding: 0;
}

.preview-img {
    width: 100%;
    max-height: 40px;
    max-width: 40px;
    height: 100%;
    display: block;
    object-fit: cover;
}

.drop-area {
    width: 100%;   
    margin: 0 auto;
    padding: 40px 20px;
    cursor: pointer;
    color: var(--bs-primary-text-emphasis);
    background: var(--bs-primary-bg-subtle);
    border-color: var(--bs-primary);
    border-radius: 10px;
    // box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
    border: 1px dashed;
    transition: .2s ease;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    &[data-active=true] {

        &.success {
            background: var(--bs-success-bg-subtle);
            color: var(--bs-success-text-emphasis);
            border-color: var(--bs-success);
        }

        &.warning {
            background: var(--bs-warning-bg-subtle);
            color: var(--bs-warning-text-emphasis);
            border-color: var(--bs-warning);
        }

        &.danger {
            background: var(--bs-danger-bg-subtle);
            color: var(--bs-danger-text-emphasis);
            border-color: var(--bs-danger);
        }

        font-weight: bold;

        button {
            visibility: hidden;
        }
    }
}

.upload-input {
    width: 0px;
    height: 0px;
    overflow: hidden;
}
</style>